import { Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { usePunchListActivityLog } from '../hooks/punch-list-hooks';
import type { ActivityLogEntry } from '../types';

interface PunchListActivityFeedProps {
  punchlistId: string;
}

const ACTION_ACCENT: Record<ActivityLogEntry['action_type'], string> = {
  item_completed: 'bg-green-100 text-green-700 border-green-300',
  status_change: 'bg-blue-100 text-blue-700 border-blue-300',
  comment: 'bg-gray-100 text-gray-700 border-gray-300',
  section_added: 'bg-purple-100 text-purple-700 border-purple-300',
  created: 'bg-indigo-100 text-indigo-700 border-indigo-300',
};

const PunchListActivityFeed = ({ punchlistId }: PunchListActivityFeedProps) => {
  const { data: entries, isLoading } = usePunchListActivityLog(punchlistId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!entries || entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-6">No activity yet</p>
    );
  }

  return (
    <div className="relative">
      <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" />

      <div className="space-y-6">
        {entries.map((entry) => {
          const accent = ACTION_ACCENT[entry.action_type] ?? ACTION_ACCENT.comment;

          return (
            <div key={entry.id} className="relative flex gap-3 pl-0">
              <div
                className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${accent}`}
              >
                {entry.actor.initials}
              </div>

              <div className="min-w-0 flex-1 pt-0.5">
                <p className="text-sm">
                  <span className="font-medium">{entry.actor.name}</span>{' '}
                  <span className="text-muted-foreground">{entry.description}</span>
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {formatDistanceToNow(new Date(entry.timestamp), { addSuffix: true })}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PunchListActivityFeed;
